import { useEffect, useRef, useState } from "react";
import { useAlert } from "react-alert";
import { createChart, AreaSeries } from "lightweight-charts";
import api from "../../api";

const BAR_HEIGHTS = [38, 54, 30, 66, 46, 72, 40, 58, 84, 50, 68, 44, 78, 56, 88, 62];

export const BTCCalculator = () => {
  const alert = useAlert();
  const [data, setData] = useState({
    amount: 100,
  });
  const [loader, setLoader] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({});

  const chartRef = useRef(null);
  const [livePrice, setLivePrice] = useState(null);
  const [priceChangePct, setPriceChangePct] = useState(null);

  const onTextChange = (e, AmountVal) => {
    if (AmountVal) {
      setData({ ...data, amount: AmountVal });
    } else {
      setData({ ...data, [e.target.name]: e.target.value });
    }
  };

  const checkData = async () => {
    try {
      if (!data?.date) {
        alert.show("PlEASE ADD DATE.");
        return;
      }
      if (!data?.symbol) {
        alert.show("PLEASE SELECT COIN.");
        return;
      }
      if (!data?.amount) {
        alert.show("PLEASE ENTER AMOUNT.");
        return;
      }
      if (data?.amount < 0) {
        alert.show("AMOUNT MUST BE GREATER THAN 0.");
        return;
      }
      setLoader(true);
      let payload = {
        symbol: data?.symbol,
        date: data?.date,
      };
      const res = await api.getCoinCalculator(payload);
      // console.log(res);
      if (res?.success) {
        dataCalculations(data?.amount, res?.purchasePrice, res?.currrentPrice);
      }
      setLoader(false);
    } catch (e) {
      if (
        e.message == "server not responding" ||
        e.message == "Network Error"
      ) {
        alert.show("PLEASE TRY AGAIN SHORTLY...");
      } else if (e?.message) {
        alert.show(e?.message || "Too many requests. Please wait.");
      } else {
        console.log(e?.response?.data);

        alert.show(e?.response?.data || "PLEASE TRY AGAIN SHORTLY...");
      }
      // alert.show("Invalid Credentials or Account not verified");
      setLoader(false);
    }
  };

  const dataCalculations = (initialInvestment, purchasePrice, currentPrice) => {
    const btcBought = initialInvestment / purchasePrice;
    const currentValue = btcBought * currentPrice;
    const profit = currentValue - initialInvestment;
    const returnPct = (profit / initialInvestment) * 100;
    let payload = {
      btcBought,
      currentValue,
      profit,
      returnPct,
      entryPrice: purchasePrice,
    };
    setCalculatedValues(payload);
  };

  function formatPrice(value) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: value < 1 ? 4 : 2,
      maximumFractionDigits: value < 1 ? 8 : 2,
    }).format(value);
  }

  // ── Live BTC/USDT price + mini chart (same source as the Live BTC/USDT Chart section) ──
  useEffect(() => {
    const chart = createChart(chartRef.current, {
      layout: {
        textColor: "#8a86a0",
        background: { type: "solid", color: "transparent" },
      },
      width: chartRef.current.clientWidth,
      height: 120,
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false },
      handleScroll: false,
      handleScale: false,
    });

    const areaSeries = chart.addSeries(AreaSeries, {
      topColor: "rgba(137, 90, 239, 0.35)",
      bottomColor: "rgba(137, 90, 239, 0)",
      lineColor: "#895aef",
      lineWidth: 2,
    });

    chart.timeScale().fitContent();

    fetch(
      "https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=120",
    )
      .then((res) => res.json())
      .then((klines) => {
        const formatted = klines.map((d) => ({
          time: d[0] / 1000,
          value: parseFloat(d[4]),
        }));
        areaSeries.setData(formatted);
      })
      .catch(() => {});

    const wsTrades = new WebSocket(
      "wss://stream.binance.com:9443/ws/btcusdt@trade",
    );
    wsTrades.onmessage = (event) => {
      const trade = JSON.parse(event.data);
      const lastPrice = parseFloat(trade.p);
      const time = Math.floor(trade.T / 1000);
      setLivePrice(lastPrice);
      areaSeries.update({ time, value: lastPrice });
    };

    const wsTicker = new WebSocket(
      "wss://stream.binance.com:9443/ws/btcusdt@ticker",
    );
    wsTicker.onmessage = (event) => {
      const stats = JSON.parse(event.data);
      setPriceChangePct(parseFloat(stats.P));
    };

    const handleResize = () => {
      if (chartRef.current) {
        chart.applyOptions({ width: chartRef.current.clientWidth });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      wsTrades.close();
      wsTicker.close();
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, []);

  const isUp = (priceChangePct || 0) >= 0;
  const holdYears =
    calculatedValues?.entryPrice && data?.date
      ? ((Date.now() - new Date(data.date).getTime()) / 31557600000).toFixed(1)
      : null;
  const multiple =
    calculatedValues?.entryPrice && data?.amount
      ? (calculatedValues.currentValue / data.amount).toFixed(2)
      : null;

  return (
    <section className="wzCalc">
      <div className="wzCalc-gridbg"></div>
      <div className="wzCalc-orb o1"></div>
      <div className="wzCalc-orb o2"></div>

      <div className="wzCalc-wrap">
        <div className="wzCalc-head">
          <div className="wzCalc-badge">
            <i></i>Live Market Simulator
          </div>
          <h1 className="wzCalc-h1">
            If I Bought Bitcoin
            <br />
            <span>Calculator</span>
          </h1>
          <p className="wzCalc-sub">
            Pick an amount, a date, and a coin. See what that trade would be
            worth today.
          </p>
        </div>

        <div className="wzCalc-frame">
          <div className="wzCalc-panel">
            <div className="wzCalc-grid">
              <div className="wzCalc-visual">
                <div className="wzCalc-spot">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="k">BTC / USDT</div>
                    <div className="wzCalc-price-row">
                      <span className="wzCalc-price">
                        {livePrice
                          ? `$${livePrice.toLocaleString()}`
                          : "Loading…"}
                      </span>
                      {priceChangePct != null && (
                        <span
                          className={`wzCalc-delta ${isUp ? "up" : "down"}`}
                        >
                          {isUp ? "+" : ""}
                          {priceChangePct.toFixed(2)}% 24h
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="wzCalc-coin-orbit">
                    <div className="ring"></div>
                    <div className="coin">₿</div>
                  </div>
                </div>

                <div className="wzCalc-chart" ref={chartRef} />

                <div className="wzCalc-bars">
                  {BAR_HEIGHTS.map((h, i) => (
                    <i key={i} style={{ height: `${h}%` }}></i>
                  ))}
                </div>

                <div className="wzCalc-ticker">
                  <div>
                    <div className="k">ENTRY PRICE</div>
                    <div className="v">
                      {calculatedValues?.entryPrice
                        ? `$${formatPrice(calculatedValues.entryPrice)}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="k">HOLD PERIOD</div>
                    <div className="v">{holdYears ? `${holdYears}y` : "—"}</div>
                  </div>
                  <div>
                    <div className="k">MULTIPLE</div>
                    <div className="v">{multiple ? `${multiple}x` : "—"}</div>
                  </div>
                </div>
              </div>

              <div className="wzCalc-form">
                <div>
                  <label className="wzCalc-label c-amount">
                    Enter the Amount
                  </label>
                  <div className="wzCalc-field">
                    <span className="wzCalc-currency">$</span>
                    <input
                      onChange={(e) => onTextChange(e)}
                      type="number"
                      name="amount"
                      placeholder="100"
                      className="wzCalc-input amount"
                      value={data?.amount}
                    />
                  </div>
                  <div className="wzCalc-presets">
                    {[100, 200, 300].map((v) => (
                      <div
                        key={v}
                        className={
                          data?.amount == v
                            ? "wzCalc-chip is-active"
                            : "wzCalc-chip"
                        }
                        onClick={(e) => onTextChange(e, v)}
                      >
                        ${v}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="wzCalc-label c-date">
                    Enter the Date
                  </label>
                  <div className="wzCalc-field">
                    <span className="ic">
                      <i className="fa-regular fa-calendar" />
                    </span>
                    <input
                      onChange={(e) => onTextChange(e)}
                      type="date"
                      name="date"
                      placeholder="date..."
                      className="wzCalc-input date"
                      value={data?.date}
                      min="2015-01-01"
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>

                <div>
                  <label className="wzCalc-label c-coin">Select Crypto</label>
                  <div className="wzCalc-field">
                    <span className="wzCalc-coin-badge">₿</span>
                    <select
                      name="symbol"
                      onChange={(e) => onTextChange(e)}
                      className="wzCalc-select"
                    >
                      <option value="">Select currency</option>
                      <option value="btc">BITCOIN</option>
                      {/* <option value="eth">ETHEREUM</option> */}
                    </select>
                    <span className="ic">
                      <i className="fa-solid fa-caret-down" />
                    </span>
                  </div>
                </div>

                {loader ? (
                  <button className="wzCalc-submit" disabled>
                    <span className="wzCalc-shine"></span>
                    <span className="label">
                      <i
                        className="fa fa-spinner fa-pulse me-2"
                        style={{ position: "relative", top: "-0px" }}
                      ></i>
                      Calculating…
                    </span>
                  </button>
                ) : (
                  <button className="wzCalc-submit" onClick={checkData}>
                    <span className="wzCalc-shine"></span>
                    <span className="label">Calculate Return</span>
                  </button>
                )}

                <div className="wzCalc-note">
                  <i></i>
                  <span>
                    Historical prices, illustrative only — not financial
                    advice.
                  </span>
                </div>
              </div>
            </div>

            <div className="wzCalc-results">
              <div className="wzCalc-results-row">
                <div className="wzCalc-stat">
                  <div className="top">
                    <span
                      className="dot"
                      style={{
                        background: "#764ba2",
                        boxShadow: "0 0 8px #764ba266",
                      }}
                    ></span>
                    <span className="lbl">You Would Have Bought</span>
                  </div>
                  <div className="val" style={{ color: "#764ba2" }}>
                    {(calculatedValues?.btcBought &&
                      formatPrice(calculatedValues?.btcBought)) ||
                      0}{" "}
                    {data?.symbol || ""}
                  </div>
                  <div className="sub">
                    {calculatedValues?.entryPrice
                      ? `at $${formatPrice(calculatedValues.entryPrice)}`
                      : "awaiting input"}
                  </div>
                </div>

                <div className="wzCalc-stat">
                  <div className="top">
                    <span
                      className="dot"
                      style={{
                        background: "#2563eb",
                        boxShadow: "0 0 8px #2563eb66",
                      }}
                    ></span>
                    <span className="lbl">It's Now Worth</span>
                  </div>
                  <div className="val" style={{ color: "#2563eb" }}>
                    $
                    {(calculatedValues?.currentValue &&
                      formatPrice(calculatedValues?.currentValue)) ||
                      0}
                  </div>
                  <div className="sub">
                    {calculatedValues?.entryPrice
                      ? "based on current price"
                      : "awaiting input"}
                  </div>
                </div>

                <div className="wzCalc-stat">
                  <div className="top">
                    <span
                      className="dot"
                      style={{
                        background:
                          calculatedValues?.profit >= 0 ? "#059669" : "#dc2626",
                        boxShadow: `0 0 8px ${
                          calculatedValues?.profit >= 0 ? "#05966966" : "#dc262666"
                        }`,
                      }}
                    ></span>
                    <span className="lbl">Profit</span>
                  </div>
                  <div
                    className="val"
                    style={{
                      color:
                        calculatedValues?.profit >= 0 ? "#059669" : "#dc2626",
                    }}
                  >
                    $
                    {(calculatedValues?.profit &&
                      formatPrice(calculatedValues?.profit)) ||
                      0}
                  </div>
                  <div className="sub">
                    {calculatedValues?.entryPrice
                      ? `on $${data?.amount || 0} invested`
                      : "awaiting input"}
                  </div>
                </div>

                <div className="wzCalc-stat">
                  <div className="top">
                    <span
                      className="dot"
                      style={{
                        background:
                          calculatedValues?.returnPct >= 0
                            ? "#059669"
                            : "#dc2626",
                        boxShadow: `0 0 8px ${
                          calculatedValues?.returnPct >= 0
                            ? "#05966966"
                            : "#dc262666"
                        }`,
                      }}
                    ></span>
                    <span className="lbl">Return</span>
                  </div>
                  <div
                    className="val"
                    style={{
                      color:
                        calculatedValues?.returnPct >= 0
                          ? "#059669"
                          : "#dc2626",
                    }}
                  >
                    {(calculatedValues?.returnPct &&
                      formatPrice(calculatedValues?.returnPct)) ||
                      0}
                    %
                  </div>
                  <div className="sub">
                    {calculatedValues?.entryPrice
                      ? "total return"
                      : "awaiting input"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
